import React, { useState } from "react";
import {
  Typography,
  Chip,
  Paper,
  Stack,
  Grid2,
  Switch,
  FormControlLabel,
  alpha,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  LocalShipping,
  Inventory2,
  CheckCircle,
  HourglassEmpty,
  AssignmentTurnedIn,
  Autorenew,
  Cancel,
  CreditCardOff,
  HelpOutline,
  PendingActions,
  Replay,
  Warehouse,
  AccessTime,
} from "@mui/icons-material";
import { useGetLocale } from "@refinedev/core";
import { useTranslation } from "../../../hooks/useTranslation";
import { Order } from "../../orders/utils/types";
import { Copyable } from "../../../components/copyable/Copyable";

/* =========================
   TYPES
========================= */

type HistoryItem = {
  type: "SupplyOrder" | "DeliveryOrder";
  id: number;
  amazonOrderId: string;
  status: string;
  updated: string;
};

/* =========================
   HELPERS
========================= */

const getTypeIcon = (type: HistoryItem["type"]) =>
  type === "SupplyOrder" ? <Inventory2 /> : <LocalShipping />;

const getStatusColor = (status: string) => {
  switch (status) {
    case "DELIVERED":
    case "DELIVERED_TO_BUYER":
      return "success";

    case "SHIPPING":
    case "SHIPPED_TO_BUYER":
      return "info";

    case "ACCEPTED":
    case "CONFIRMED":
      return "primary";

    case "CANCELLED":
    case "PAYMENT_DECLINED":
      return "error";

    case "PENDING":
    case "WAITING_FOR_ADDRESS":
    case "WAITING_FOR_PURCHASE":
    case "WAITING_FOR_SHIPMENT_TO_WAREHOUSE":
      return "secondary";

    default:
      return "default";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "WAITING_FOR_ADDRESS":
      return <PendingActions fontSize="small" />;
    case "WAITING_FOR_PURCHASE":
      return <HourglassEmpty fontSize="small" />;
    case "WAITING_FOR_SHIPMENT_TO_WAREHOUSE":
      return <LocalShipping fontSize="small" />;
    case "ON_WAREHOUSE":
      return <Warehouse fontSize="small" />;
    case "SHIPPED_TO_BUYER":
      return <LocalShipping fontSize="small" />;
    case "DELIVERED_TO_BUYER":
      return <CheckCircle fontSize="small" />;
    case "ACCEPTED":
      return <AssignmentTurnedIn fontSize="small" />;
    case "SHIPPING":
      return <LocalShipping fontSize="small" />;
    case "PENDING":
      return <HourglassEmpty fontSize="small" />;
    case "WAITING_FOR_MANUAL_CANCELLATION":
      return <Autorenew fontSize="small" />;
    case "TO_BE_REFUNDED":
      return <Replay fontSize="small" />;
    case "CANCELLED":
      return <Cancel fontSize="small" />;
    case "UNKNOWN":
      return <HelpOutline fontSize="small" />;
    case "CONFIRMED":
      return <AssignmentTurnedIn fontSize="small" />;
    case "REPACKAGING":
      return <Autorenew fontSize="small" />;
    case "STORED_ON_WAREHOUSE":
      return <Inventory2 fontSize="small" />;
    case "PAYMENT_DECLINED":
      return <CreditCardOff fontSize="small" />;
    case "REFUNDED":
    case "PARTIALLY_REFUNDED":
      return <Replay fontSize="small" />;
    case "DELIVERED":
      return <CheckCircle fontSize="small" />;
    default:
      return <HourglassEmpty fontSize="small" />;
  }
};

/* =========================
   INDEX MAP
========================= */

const buildSupplyOrderIndexMap = (items: HistoryItem[]) => {
  const map = new Map<string, number>();
  let counter = 1;

  items.forEach((item) => {
    if (item.type !== "SupplyOrder") return;

    if (!map.has(item.amazonOrderId)) {
      map.set(item.amazonOrderId, counter++);
    }
  });

  return map;
};

/* =========================
   TIMELINE
========================= */

const renderTimeline = (
  items: HistoryItem[],
  showType: boolean,
  locale: string | undefined,
  translate: (_key: string) => string,
  translateWithoutPrefix: (_key: string) => string,
) => {
  const supplyOrderIndexMap = buildSupplyOrderIndexMap(items);

  return (
    <Timeline position="alternate-reverse">
      {items.map((item, index) => {
        const supplyIndex =
          item.type === "SupplyOrder"
            ? supplyOrderIndexMap.get(item.amazonOrderId)
            : null;

        const statusKey =
          item.type === "SupplyOrder"
            ? `supply.${item.status}`
            : `delivery.${item.status}`;

        const translatedStatus = translateWithoutPrefix("common." + statusKey);
        const safeStatus =
          translatedStatus === statusKey
            ? item.status.replaceAll("_", " ")
            : translatedStatus;

        return (
          <TimelineItem key={`${item.type}-${item.id}-${index}`}>
            <TimelineSeparator>
              <TimelineConnector />
              <TimelineDot variant="outlined" color={"primary"}>
                {getTypeIcon(item.type)}
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>

            <TimelineContent>
              <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                {/* HEADER */}
                <Stack
                  direction="row"
                  alignItems={"center"}
                  justifyContent="space-between"
                  px={1.5}
                  py={0.75}
                  sx={(theme) => {
                    const colorKey = getStatusColor(item.status);

                    const paletteColor =
                      colorKey === "default"
                        ? {
                            main: theme.palette.grey[500],
                            contrastText: "#fff",
                          }
                        : theme.palette[colorKey];

                    const base = paletteColor.main;
                    const contrast = paletteColor.contrastText;

                    const isDark = theme.palette.mode === "dark";

                    return {
                      background: isDark
                        ? `linear-gradient(90deg, ${alpha(base, 0.3)}, ${alpha(base, 0.15)})`
                        : `linear-gradient(90deg, ${alpha(base, 0.9)}, ${alpha(base, 0.7)})`,

                      color: contrast,
                    };
                  }}
                >
                  {showType || supplyIndex !== null ? (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={(theme) => {
                        const isDark = theme.palette.mode === "dark";

                        return {
                          color: isDark ? "#fff" : "#000",

                          ".MuiChip-icon": {
                            color: "inherit",
                          },
                        };
                      }}
                    >
                      {item.type === "SupplyOrder" && supplyIndex
                        ? `${translate(`types.${item.type}`)} ${supplyIndex}`
                        : translate(`types.${item.type}`)}
                    </Typography>
                  ) : (
                    <span />
                  )}

                  <Chip
                    size="small"
                    label={safeStatus}
                    icon={getStatusIcon(item.status)}
                    sx={(theme) => {
                      const isDark = theme.palette.mode === "dark";

                      return {
                        fontWeight: 500,

                        color: isDark ? "#fff" : "#000", // 🔥 kritik fix

                        ".MuiChip-icon": {
                          color: "inherit",
                        },
                      };
                    }}
                  />
                </Stack>

                {/* CONTENT */}
                <Stack py={2} spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    {translate("labels.amazonOrderId")}
                  </Typography>

                  <Copyable initialLabel={item.amazonOrderId} />
                </Stack>

                {/* FOOTER */}
                <Stack direction="row" p={1} justifyContent="space-between">
                  <AccessTime fontSize="small" />

                  <Typography variant="caption" fontWeight={600}>
                    {new Date(item.updated).toLocaleDateString(locale)}{" "}
                    {new Date(item.updated).toLocaleTimeString(locale)}
                  </Typography>
                </Stack>
              </Paper>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

/* =========================
   COMPONENT
========================= */

export const SupplyOrderHistory = ({ order }: { order: Order }) => {
  const [splitMode, setSplitMode] = useState(false);

  const getLocale = useGetLocale();
  const locale = getLocale();

  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail.supplyOrderHistory",
  );

  const rawHistory = Array.isArray(order.history) ? order.history : [];

  const sortedHistory = [...rawHistory].sort(
    (a, b) => new Date(a.updated).getTime() - new Date(b.updated).getTime(),
  );

  const supplyOrders = sortedHistory.filter((i) => i.type === "SupplyOrder");
  const deliveryOrders = sortedHistory.filter(
    (i) => i.type === "DeliveryOrder",
  );

  return (
    <Grid2 component={!splitMode ? Paper : "div"} size={{ xs: 12 }} p={1}>
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography variant="h6">{translate("title")}</Typography>

        <FormControlLabel
          label={translate(splitMode ? "split" : "combined")}
          control={
            <Switch
              size="small"
              checked={splitMode}
              onChange={(e) => setSplitMode(e.target.checked)}
            />
          }
        />
      </Stack>

      {!splitMode &&
        renderTimeline(
          sortedHistory,
          true,
          locale,
          translate,
          translateWithoutPrefix,
        )}

      {splitMode && (
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 6 }}>
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle2">
                {translate("sections.supplyOrders")}
              </Typography>
              {renderTimeline(
                supplyOrders,
                false,
                locale,
                translate,
                translateWithoutPrefix,
              )}
            </Paper>
          </Grid2>

          <Grid2 size={{ xs: 6 }}>
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle2">
                {translate("sections.deliveryOrders")}
              </Typography>
              {renderTimeline(
                deliveryOrders,
                false,
                locale,
                translate,
                translateWithoutPrefix,
              )}
            </Paper>
          </Grid2>
        </Grid2>
      )}
    </Grid2>
  );
};
